import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useIntl } from '@grandvisionhq/state';
import { useScrollIntoView, DateUtils } from '@grandvisionhq/utils';
import { viewStoresNearbyClickEvent, chooseAnotherStoreClickEvent, clickOnAlternativeLocationEvent } from '../../data-layer/actions.js';
import useDataLayerInput from '../../hooks/use-data-layer-input.js';
import { useAppointmentContext } from '../../pages/BookAppointment/context/AppointmentContext.js';
import { useRoutingContext } from '../../pages/BookAppointment/context/RoutingContext/context.js';
import '@grandvisionhq/graphql';
import '../../pages/BookAppointment/queries/get-book-appointment-store-by-id.js';
import '../../pages/BookAppointment/queries/get-book-appointment-type.js';
import '@grandvisionhq/bloomreach';
import useVirtualAppointment from '../../pages/BookAppointment/hooks/use-virtual-appointment.js';
import { useAvailabilityStoresNearby } from '../../hooks/api/use-availability-stores-nearby.js';
import AlternativeStoreSuggestions from './AlternativeStoreSuggestions.js';
import useGetAvailabilityStoresNearbyQueryVariables from './useGetAvailabilityStoresNearbyQueryVariables.js';
import useGetTimeslotLabel from './useGetTimeslotLabel.js';
import useResetUiStateForNewDateStoreOrTimeFilters from './useResetUiStateForNewDateStoreOrTimeFilters.js';

const AlternativeStoreSuggestionsContainer = (_param)=>{
    var { className , requestStoresFromParent , selectedDate , selectedStore , onClickStore  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "requestStoresFromParent",
        "selectedDate",
        "selectedStore",
        "onClickStore"
    ]);
    const { getLabel  } = useIntl();
    const storesRequestedSelectedDate = React.useRef();
    const dateThatDataReferTo = React.useRef();
    const storeThatDataReferTo = React.useRef();
    const [getAvailabilityStoresNearby, availabilityStoresNearby] = useAvailabilityStoresNearby();
    const [{ goTo  }, { queryParams  }] = useRoutingContext();
    const [{ setStore , setTimeSlot  }, { type  }] = useAppointmentContext();
    const [{ isVirtualAppointmentType  }] = useVirtualAppointment();
    const getTimeslotLabel = useGetTimeslotLabel(selectedDate);
    const { elementRef , triggerScroll  } = useScrollIntoView({
        block: 'end'
    });
    const [uiState, setUiState] = React.useState('prompt-for-stores');
    const queryVariables = useGetAvailabilityStoresNearbyQueryVariables(selectedDate);
    useResetUiStateForNewDateStoreOrTimeFilters(()=>setUiState('prompt-for-stores')
    , selectedDate);
    const dataLayerInput = useDataLayerInput();
    const requestStores = React.useCallback(()=>{
        if (!queryVariables) {
            // this should never happen
            console.error('No query variables provided to request stores');
            return;
        }
        DataLayer.pushEvent(viewStoresNearbyClickEvent(dataLayerInput));
        getAvailabilityStoresNearby({
            variables: queryVariables
        });
        dateThatDataReferTo.current = queryVariables.startDate;
        storeThatDataReferTo.current = `${queryVariables.latitude},${queryVariables.longitude}`;
    }, [
        dataLayerInput,
        getAvailabilityStoresNearby,
        queryVariables
    ]);
    const onClickPromptCta = React.useCallback(()=>{
        requestStores();
        setTimeout(()=>{
            triggerScroll();
        }, 50);
    }, [
        requestStores,
        triggerScroll
    ]);
    React.useEffect(()=>{
        // no need to check for store here because we will only jump to stores that have availability
        const haveNotRequestedStoresForSelectedDate = storesRequestedSelectedDate.current !== selectedDate;
        if (haveNotRequestedStoresForSelectedDate && requestStoresFromParent) {
            requestStores();
            storesRequestedSelectedDate.current = selectedDate;
        }
    }, [
        requestStores,
        requestStoresFromParent,
        selectedDate
    ]);
    const validStores = React.useMemo(()=>{
        var ref;
        return (ref = availabilityStoresNearby.data) === null || ref === void 0 ? void 0 : ref.filter(// if no slots and no next available date, then the store is not valid
        (item)=>!(item.slotsAvailable === 0 && item.nextAvailableBookingDate === undefined)
        );
    }, [
        availabilityStoresNearby.data
    ]);
    React.useEffect(()=>{
        const date = selectedDate ? DateUtils.toLocalDate(selectedDate).split('T')[0] : undefined;
        const dataDoNotReferToSelectedDate = dateThatDataReferTo.current !== date;
        const { lat , lon  } = selectedStore !== null && selectedStore !== void 0 ? selectedStore : {};
        const dataDoNotReferToSelectedStore = storeThatDataReferTo.current !== `${lat},${lon}`;
        if (availabilityStoresNearby.called === false || dataDoNotReferToSelectedDate || dataDoNotReferToSelectedStore) {
            return;
        }
        if (availabilityStoresNearby.loading) {
            setUiState('loading-stores');
        } else if (!!validStores && validStores.length > 0) {
            setUiState('stores-available');
        } else {
            setUiState('no-stores-available');
        }
    }, [
        availabilityStoresNearby,
        selectedDate,
        selectedStore,
        validStores
    ]);
    const returnToWhereStep = React.useCallback(()=>{
        const { typeId , search , userLocation  } = queryParams;
        setStore(null);
        setTimeSlot(null);
        DataLayer.pushEvent(chooseAnotherStoreClickEvent(dataLayerInput));
        goTo('where', {
            typeId,
            search,
            userLocation
        });
        return;
    }, [
        dataLayerInput,
        goTo,
        queryParams,
        setStore,
        setTimeSlot
    ]);
    const labels = React.useMemo(()=>({
            title: getLabel(`appointments.alternativeStoreSuggestions.title`),
            prompt: getLabel(`appointments.alternativeStoreSuggestions.prompt`),
            promptCta: getLabel(`appointments.alternativeStoreSuggestions.promptCta`),
            noStoresAvailable: getLabel(`appointments.alternativeStoreSuggestions.noStoresAvailable`),
            noStoresAvailableCta: getLabel(`appointments.alternativeStoreSuggestions.noStoresAvailableCta`)
        })
    , [
        getLabel
    ]);
    const onClickStoreCard = React.useCallback((store, date)=>{
        onClickStore === null || onClickStore === void 0 ? void 0 : onClickStore(store, date);
        DataLayer.pushEvent(clickOnAlternativeLocationEvent(swcHelpers.objectSpread({}, dataLayerInput, {
            store: store && !isVirtualAppointmentType(type) ? swcHelpers.objectSpread({}, store, {
                nextAvailableBookingDate: date
            }) : undefined
        })));
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.setTimeout(()=>{
            window.scrollTo({
                top: 0,
                behavior: prefersReducedMotion ? 'auto' : 'smooth'
            });
        }, 100);
    }, [
        dataLayerInput,
        isVirtualAppointmentType,
        onClickStore,
        type
    ]);
    return /*#__PURE__*/ React.createElement(AlternativeStoreSuggestions, swcHelpers.extends({
        className: className,
        ref: elementRef,
        labels: labels,
        uiState: uiState,
        stores: validStores,
        onClickPromptCta: onClickPromptCta,
        onClickNoStoresAvailableCta: returnToWhereStep,
        getTimeslotLabel: getTimeslotLabel,
        onClickStore: onClickStoreCard
    }, rest));
};

export { AlternativeStoreSuggestionsContainer as default };
//# sourceMappingURL=AlternativeStoreSuggestionsContainer.js.map
